import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import {
  useGetActiveProfileDefinition,
  useGetUserToolboxProfile,
} from "../../api/queries";
import { useUpsertUserToolboxProfile } from "../../api/mutations";
import { useNotification } from "../../hooks/useNotification";
import Button from "../../componentLibrary/Button";
import Spinner from "../../componentLibrary/Spinner";
import type { ToolboxAnswers, ToolboxAnswerValue } from "../../types";
import { renderProfileQuestion } from "./components/ProfileQuestion";

export default function ToolboxProfilePage() {
  const { topicId } = useParams<{ topicId: string }>();
  const { notify } = useNotification();
  const navigate = useNavigate();

  const [answers, setAnswers] = useState<ToolboxAnswers>({});
  const lastAnswersInitKey = useRef<string | null>(null);

  const {
    data: definition,
    isLoading: definitionLoading,
    isError: definitionError,
  } = useGetActiveProfileDefinition(topicId ?? "");

  const { data: existingProfile, isLoading: profileLoading } =
    useGetUserToolboxProfile(topicId ?? "");

  const { mutate: upsertProfile, isPending: isSaving } =
    useUpsertUserToolboxProfile({
      onSuccess: async () => {
        notify("success", "Profile saved.");
        navigate(`/toolboxes/${topicId}`);
      },
      onError: () => {
        notify("error", "Failed to save profile. Please try again.");
      },
    });

  // Pre-populate answers from existing profile once definition + profile query have settled.
  useEffect(() => {
    if (!definition || !topicId || profileLoading) return;
    const profileStamp = existingProfile?._id ?? "none";
    const key = `${topicId}:${definition._id}:${profileStamp}`;
    if (lastAnswersInitKey.current === key) return;
    lastAnswersInitKey.current = key;
    setAnswers(existingProfile?.answers ?? {});
  }, [topicId, definition, profileLoading, existingProfile]);

  const handleChange = (variableName: string, value: ToolboxAnswerValue) => {
    setAnswers((prev) => ({ ...prev, [variableName]: value }));
  };

  const handleSave = () => {
    if (!topicId) return;
    upsertProfile({ topicId, answers });
  };

  const isLoading = definitionLoading || profileLoading;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="w-8" />
      </div>
    );
  }

  if (definitionError || !definition) {
    return (
      <div className="space-y-4 pt-4">
        <Link
          to=".."
          relative="path"
          className="flex items-center gap-1.5 text-sm text-electrum-300 hover:text-electrum transition-colors mb-4 w-fit"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to toolbox
        </Link>
        <div className="flex items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 py-16">
          <p className="text-sm text-gray-400">
            No profile questions have been set up for this topic yet.
          </p>
        </div>
      </div>
    );
  }

  const answeredCount = Object.values(answers).filter(
    (v) => v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0),
  ).length;

  return (
    <div className="space-y-6 pt-4 max-w-2xl mx-auto">
      <div className="flex items-center justify-between gap-3">
        <Link
          to=".."
          relative="path"
          className="flex items-center gap-1.5 text-sm text-electrum-300 hover:text-electrum transition-colors mb-4 w-fit"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to toolbox
        </Link>
        <span className="text-xs text-gray-400">
          {answeredCount} of {definition.questions.length} answered
        </span>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-gray-800">Your Profile</h1>
        <p className="text-sm text-gray-500 mt-1">
          Answer the questions below to build your toolbox profile.
        </p>
      </div>

      <div className="space-y-6">
        {definition.questions.map((question) =>
          renderProfileQuestion(question, answers, handleChange),
        )}
      </div>

      {definition.questions.length > 0 && (
        <div className="pt-2 pb-8">
          <Button
            type="button"
            loading={isSaving}
            onClick={handleSave}
            className="w-full"
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
}
